import { hasValue, RoleNameMap } from '@r-and-a-shared-ui/utils';
import {
  ContactRolesByPriority,
  ChampionshipContact,
  UserRole,
} from 'libs/utils/src/lib/player-portals/constants';
import { parsePPContactIdentifier } from 'libs/utils/src/lib/player-portals';
import { usePlayerPortalsConfigContext } from '../../../contexts/PlayerPortalsConfig/PlayerPortalsConfigContext';
import { PPAttributes } from 'libs/auth-client/src/types/pp-user-attribute-types';
import { parseUserRole } from './parseUserRole';
import {
  AUTH_CONTEXT_KEYS,
  useAuthContext,
} from '@r-and-a-shared-ui/auth-client';

const getHighestRoleFromArray = (roles: string[]): string | undefined => {
  const parsed = roles
    .map(r => parseUserRole(r))
    .filter((r): r is UserRole => r !== undefined);

  if (!hasValue(parsed)) {
    return undefined;
  }

  const highest = parsed.reduce((best, current) =>
    ContactRolesByPriority[current as keyof typeof ContactRolesByPriority] >
    ContactRolesByPriority[best as keyof typeof ContactRolesByPriority]
      ? current
      : best,
  );

  return RoleNameMap[highest];
};

const getHighestContactRoleForChampionship = (
  ppContacts: ChampionshipContact[],
  championshipId: string | undefined,
  allowedRoles?: string[],
): string | undefined => {
  const champ = ppContacts.find(c => c.championshipId === championshipId);

  if (!hasValue(champ)) {
    return undefined;
  }

  let roles = champ!.contactIdentifiers.map(c => c.role);

  if (hasValue(allowedRoles)) {
    const allowedEnums = new Set(
      allowedRoles!
        .map(r => parseUserRole(r))
        .filter((r): r is UserRole => hasValue(r)),
    );

    roles = roles.filter(r => {
      const parsed = parseUserRole(r);
      return hasValue(parsed) && allowedEnums.has(parsed!);
    });
  }

  return getHighestRoleFromArray(roles);
};

export const getPPContactToDisplay = (customRoles?: string[]) => {
  const { userState } = useAuthContext(AUTH_CONTEXT_KEYS.PP_CONTEXT_KEY);

  const { currentChampionship } = usePlayerPortalsConfigContext();
  const currentChampionshipId = currentChampionship?.championshipIdentifier;

  if (!hasValue(currentChampionshipId)) {
    return undefined;
  }

  const parsedPPContacts =
    parsePPContactIdentifier(
      userState.user?.[PPAttributes.PPContactIdentifier],
    ) ?? [];

  const role = getHighestContactRoleForChampionship(
    parsedPPContacts,
    currentChampionshipId,
    customRoles,
  );

  if (!hasValue(role)) {
    return undefined;
  }

  const targetRoleEnum = parseUserRole(role);

  if (Number.isNaN(targetRoleEnum)) {
    return undefined;
  }

  return parsedPPContacts
    .find(c => c.championshipId === currentChampionshipId)
    ?.contactIdentifiers.find(contact => {
      const contactRoleEnum = parseUserRole(contact.role);
      return contactRoleEnum === targetRoleEnum;
    });
};
